import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, FileText, AlertTriangle, Eye, Network, Lightbulb, Zap, Check } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
const reveal = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
interface SectionProps {
  num: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  id?: string;
}
function Section({
  num,
  title,
  subtitle,
  children,
  id
}: SectionProps) {
  return <motion.section id={id} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-80px'
  }} variants={reveal} className="py-16 border-t border-slate-200 first:border-t-0" data-id="element-58" data-mp="3l852cw7xfrgy">
      <div className="mb-10" data-id="element-59" data-mp="31gzze49hiez">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue mb-3" data-id="element-60" data-mp="30p4026r5io6">
          {num}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-navy" data-id="element-61" data-mp="1yw23i7oro0bt">
          {title}
        </h2>
        {subtitle && <p className="mt-3 text-lg text-slate-600 max-w-2xl leading-relaxed" data-id="element-62" data-mp="1tw61uf3jkkzf">
            {subtitle}
          </p>}
      </div>
      {children}
    </motion.section>;
}
interface SwatchProps {
  name: string;
  hex: string;
  usage: string;
  className: string;
  dark?: boolean;
}
function Swatch({
  name,
  hex,
  usage,
  className,
  dark
}: SwatchProps) {
  return <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-id="element-63" data-mp="2cvtl0iehryuk">
      <div className={`h-24 ${className} ${dark ? 'border-b border-slate-700' : ''}`} data-id="element-64" data-mp="2ctgwfme8aq8j" />
      <div className="p-4" data-id="element-65" data-mp="27uw5kdkv9iwr">
        <div className="font-semibold text-navy" data-id="element-66" data-mp="35cmksieb1jr1">{name}</div>
        <div className="text-xs font-mono text-slate-500 mt-0.5 uppercase" data-id="element-67" data-mp="35e7a9tlrici1">
          {hex}
        </div>
        <div className="text-sm text-slate-600 mt-2" data-id="element-68" data-mp="30jky3o7uwfww">{usage}</div>
      </div>
    </div>;
}
const COLORS: SwatchProps[] = [{
  name: 'Primario',
  hex: '#0F172A',
  usage: 'Fondo oscuro, titulares, consolas de datos.',
  className: 'bg-navy',
  dark: true
}, {
  name: 'Secundario',
  hex: '#2563EB',
  usage: 'Acciones primarias, enlaces, énfasis.',
  className: 'bg-techblue'
}, {
  name: 'Acento',
  hex: '#38BDF8',
  usage: 'Métricas, destellos sobre fondo oscuro.',
  className: 'bg-cyan'
}, {
  name: 'Riesgo',
  hex: '#DC2626',
  usage: 'Solo para alertas y hallazgos críticos.',
  className: 'bg-risk'
}, {
  name: 'Fondo',
  hex: '#F8FAFC',
  usage: 'Lienzo claro de las secciones narrativas.',
  className: 'bg-light'
}, {
  name: 'Superficie',
  hex: '#FFFFFF',
  usage: 'Tarjetas y paneles sobre fondo claro.',
  className: 'bg-white border-b border-slate-200'
}, {
  name: 'Texto / borde',
  hex: '#64748B',
  usage: 'Texto secundario (slate-500) y bordes sutiles.',
  className: 'bg-slate-500',
  dark: true
}, {
  name: 'Neutro oscuro',
  hex: '#1E293B',
  usage: 'Paneles y bordes dentro de consolas (slate-800).',
  className: 'bg-slate-800',
  dark: true
}];
const TYPE_SCALE = [{
  role: 'H1 — Titular',
  classes: 'text-4xl md:text-6xl font-bold tracking-tight',
  sample: 'Entiende mejor.'
}, {
  role: 'H2 — Sección',
  classes: 'text-3xl md:text-4xl font-bold tracking-tight',
  sample: 'Inteligencia accionable'
}, {
  role: 'H3 — Subtítulo',
  classes: 'text-xl font-semibold',
  sample: 'Detecta contradicciones'
}, {
  role: 'Cuerpo',
  classes: 'text-lg text-slate-600',
  sample: 'Prisma analiza cientos de documentos para revelar riesgos ocultos.'
}, {
  role: 'Etiqueta',
  classes: 'text-xs font-medium uppercase tracking-wider text-slate-500',
  sample: 'Riesgos detectados'
}];
const VALUES = ['claridad', 'entendimiento', 'ventaja', 'descubrimiento', 'visión', 'inteligencia'];
const ICONS = [ShieldAlert, FileText, AlertTriangle, Eye, Network, Lightbulb, Zap];
export function StyleGuide() {
  return <div className="min-h-screen w-full bg-light font-sans text-navy" data-id="element-69" data-mp="1japdlq5v7rm6">
      {/* Top bar */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-md" data-id="element-70" data-mp="1o8zbi8iz56bh">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between" data-id="element-71" data-mp="rjhtcu2t1rsw">
          <Logo data-id="element-72" data-mp="vja8jpmpew78" />
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-navy transition-colors focus:outline-none focus:ring-2 focus:ring-techblue rounded-sm px-1" data-id="element-73" data-mp="vkv1jatde72z">
            <ArrowLeft size={16} data-id="element-74" data-mp="15bpqimy03ks4" />
            Volver al sitio
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 max-w-5xl pb-24" data-id="element-75" data-mp="ctkej4qrmegd">
        {/* Cover */}
        <section className="py-16" data-id="element-76" data-mp="cr7m1xndslp2">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue mb-4" data-id="element-77" data-mp="5akserbx68tb">
            Design Book
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-navy leading-tight mb-6" data-id="element-78" data-mp="bhnr7zuq4lv">
            Sistema de diseño PRISMA
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed mb-8" data-id="element-79" data-mp="3ng7b5gki6n1y">
            Un prisma recibe luz compleja y la transforma en claridad. PRISMA
            convierte miles de documentos complejos en inteligencia accionable.
            Este es el lenguaje visual que sostiene esa promesa.
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-80" data-mp="38rt7j3f9iukc">
            {VALUES.map(v => <span key={v} className="px-3 py-1 bg-white text-slate-700 text-sm font-medium rounded-full border border-slate-200 capitalize" data-id="element-81" data-mp="33sge1ztvyp8s">
                {v}
              </span>)}
          </div>
        </section>

        {/* 01 Logo */}
        <Section num="01" title="Logo" subtitle="Un triángulo prisma limpio, acompañado del logotipo PRISMA en Inter bold con tracking ajustado." data-id="element-82" data-mp="2l34s6clqfjcl">
          <div className="grid sm:grid-cols-2 gap-6 mb-8" data-id="element-83" data-mp="2b5qcif7muylc">
            <div className="rounded-xl border border-slate-200 bg-white p-12 flex items-center justify-center" data-id="element-84" data-mp="2b7bcikcpv9ql">
              <Logo data-id="element-85" data-mp="33tw0rbxwb5px" />
            </div>
            <div className="rounded-xl border border-slate-800 bg-navy p-12 flex items-center justify-center" data-id="element-86" data-mp="33uxclimqjw8p">
              <Logo dark data-id="element-87" data-mp="38rnso5yjiru4" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-88" data-mp="2u4cf5yissxrd">
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-89" data-mp="2u3sxmix28bdv">
              <div className="flex items-center gap-2 text-emerald-600 font-semibold mb-3" data-id="element-90" data-mp="2z1use13ulz56">
                <Check size={18} data-id="element-91" data-mp="3mrp2iaws99lq" /> Correcto
              </div>
              <ul className="text-sm text-slate-600 space-y-1.5" data-id="element-92" data-mp="3rjgnm5x66n4h">
                <li data-id="element-93" data-mp="3rkronxz8dgrw">
                  Respeta un espacio libre mínimo igual a la altura del
                  triángulo.
                </li>
                <li data-id="element-94" data-mp="1b5wot29i5smm">
                  Azul (#2563EB) sobre claro; celeste (#38BDF8) sobre oscuro.
                </li>
                <li data-id="element-95" data-mp="1ovqcvid6o8va">Tamaño mínimo legible: ~24px de alto.</li>
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-96" data-mp="1kuy5yvrsag26">
              <div className="flex items-center gap-2 text-risk font-semibold mb-3" data-id="element-97" data-mp="x5c8qduz2awt">
                Evitar
              </div>
              <ul className="text-sm text-slate-600 space-y-1.5" data-id="element-98" data-mp="s7kbd4bk5wl8">
                <li data-id="element-99" data-mp="1662095d79f4q">No deformar ni estirar el triángulo.</li>
                <li data-id="element-100" data-mp="167lbuinwv206">No recolorear fuera de la paleta.</li>
                <li data-id="element-101" data-mp="116xn63n5mx94">No agregar sombras ni degradados al símbolo.</li>
              </ul>
            </div>
          </div>
        </Section>

        {/* 02 Color */}
        <Section num="02" title="Color" subtitle="Una base sobria de azul profundo y blanco, con acentos tecnológicos. El rojo se reserva exclusivamente para alertas." data-id="element-102" data-mp="2cmdzl9rrd31w">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-103" data-mp="31nbouotv9opm">
            {COLORS.map(c => <Swatch key={c.name} {...c} data-id="element-104" data-mp="35pf2nk40d5e2" />)}
          </div>
        </Section>

        {/* 03 Tipografía */}
        <Section num="03" title="Tipografía" subtitle="Inter en toda la interfaz. Titulares densos y confiados con tracking ajustado; cuerpo legible en slate." data-id="element-105" data-mp="3ag55oqoyow5l">
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-id="element-106" data-mp="3kd9vd0i0n9r5">
            {TYPE_SCALE.map(t => <div key={t.role} className="p-6 flex flex-col md:flex-row md:items-baseline gap-2 md:gap-8" data-id="element-107" data-mp="3pdxihb34j6g3">
                <div className="w-40 shrink-0" data-id="element-108" data-mp="1uzwnj1ap33la">
                  <div className="text-sm font-semibold text-navy" data-id="element-109" data-mp="1v17rp281ydcx">
                    {t.role}
                  </div>
                  <div className="text-xs font-mono text-slate-400 mt-1" data-id="element-110" data-mp="1rh7awaiuo6hu">
                    {t.classes}
                  </div>
                </div>
                <div className={`${t.classes} text-navy`} data-id="element-111" data-mp="1mppabnkqo7xe">{t.sample}</div>
              </div>)}
          </div>
        </Section>

        {/* 04 Botones */}
        <Section num="04" title="Botones y acciones" subtitle="Solid para la acción principal, outline sobre fondos oscuros, ghost para acciones secundarias." data-id="element-112" data-mp="1dqc89fyy2lmp">
          <div className="grid sm:grid-cols-2 gap-6" data-id="element-113" data-mp="25ggs9disl6ne">
            <div className="rounded-xl border border-slate-200 bg-white p-8 flex flex-wrap items-center gap-4" data-id="element-114" data-mp="25evu7dx9r5ks">
              <Button variant="solid" data-id="element-115" data-mp="21h7xq6fxh4qs">Solicitar Demo</Button>
              <Button variant="ghost" data-id="element-116" data-mp="1wfimoqspgr2p">Ver Ejemplo</Button>
              <Button variant="solid" size="sm" data-id="element-117" data-mp="pzyhdss3istp">
                Pequeño
              </Button>
              <Button variant="solid" size="lg" data-id="element-118" data-mp="l1eplbqtmmtf">
                Grande
              </Button>
            </div>
            <div className="rounded-xl border border-slate-800 bg-navy p-8 flex flex-wrap items-center gap-4" data-id="element-119" data-mp="9s5uk5kz09vo">
              <Button variant="solid" data-id="element-120" data-mp="4unii6bnpejg">Solicitar Demo</Button>
              <Button variant="outline" data-id="element-121" data-mp="ikzbe7g2fhbn">Ver Ejemplo</Button>
            </div>
          </div>
        </Section>

        {/* 05 Patrones */}
        <Section num="05" title="Patrones de diseño" subtitle="Secciones narrativas claras que respiran, alternadas con consolas de datos oscuras para los momentos de prueba." data-id="element-122" data-mp="dt7w4t2d0y0t">
          <div className="grid md:grid-cols-2 gap-6" data-id="element-123" data-mp="njaoqs5kk9ym">
            {/* Light card pattern */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-124" data-mp="11ik3roi1gy10">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-125" data-mp="xi07en5ahyfx">
                Tarjeta clara
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-id="element-126" data-mp="33daxeizqzgm1">
                <Network className="w-8 h-8 text-techblue mb-3" data-id="element-127" data-mp="2p56d3fsxnk17" />
                <h3 className="text-lg font-semibold" data-id="element-128" data-mp="2p82a91wyi4ol">
                  Relaciona información.
                </h3>
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-129" data-mp="2fgoen584lp0q">
                bg-white · border-slate-200 · rounded-xl · hover lift
              </p>
            </div>

            {/* Dark metric pattern */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-130" data-mp="25hfkocd9gaed">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-131" data-mp="2agjsozog8234">
                Métrica en consola
              </div>
              <div className="rounded-xl border border-slate-800 bg-navy p-6 text-center" data-id="element-132" data-mp="1wiva5yhut718">
                <div className="text-3xl font-bold text-cyan mb-1" data-id="element-133" data-mp="21gnc7lc7q9j5">2.5M</div>
                <div className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-134" data-mp="3qw39a2cv44so">
                  Documentos analizados
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-135" data-mp="3tf7y9uhcaom8">
                bg-slate-900 · border-slate-800 · número en acento
              </p>
            </div>

            {/* Alert / finding row */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-136" data-mp="3ke9v1eeq6l4p">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-137" data-mp="3ffotnbnqlveo">
                Hallazgo crítico
              </div>
              <div className="flex items-start gap-3 p-3 bg-risk/10 border border-risk/20 rounded-md" data-id="element-138" data-mp="gwrodaaovk5v">
                <div className="w-2 h-2 rounded-full bg-risk mt-1.5 shrink-0" data-id="element-139" data-mp="c0azuezkv3s9" />
                <div data-id="element-140" data-mp="77hvhs80txgp">
                  <p className="text-sm font-medium text-navy" data-id="element-141" data-mp="76oxbfn4oga6">
                    Multa excede límite legal
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-142" data-mp="27caguw9b97l">
                    Bases Administrativas · Pág 42
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-143" data-mp="uwj9duzmi0td">
                Color riesgo solo para alertas.
              </p>
            </div>

            {/* Grid/glow background */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-144" data-mp="178v4ja6o04o2">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-4" data-id="element-145" data-mp="1c6ww6ji5pcb5">
                Fondo de consola
              </div>
              <div className="relative h-28 rounded-xl border border-slate-800 bg-navy overflow-hidden" data-id="element-146" data-mp="1h46kfvtt7hxe">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-30" data-id="element-147" data-mp="1l60gtrwz1zrr" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-20 bg-techblue/20 blur-2xl rounded-full" data-id="element-148" data-mp="1v00i1ip4ukv7" />
              </div>
              <p className="text-sm text-slate-500 mt-3" data-id="element-149" data-mp="24x565o2jgqen">
                Grilla sutil + resplandor azul difuso.
              </p>
            </div>
          </div>
        </Section>

        {/* 06 Assets de publicación */}
        <Section num="06" title="Assets de publicación" subtitle="Composiciones de referencia para compartir PRISMA y mantener una presencia reconocible fuera del producto." data-id="element-150" data-mp="36khat92tyvbb">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_220px] gap-6 items-start" data-id="element-151" data-mp="3fggh4ragv6nj">
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-id="element-152" data-mp="3bdtt71gx25vm">
              <div className="flex items-center justify-between gap-4 mb-4" data-id="element-153" data-mp="2no93lzq9cadd">
                <div data-id="element-154" data-mp="2ip4oytkkdkfn">
                  <h3 className="font-semibold text-navy" data-id="element-155" data-mp="2ipxphcsxjdpc">Default Social Preview Image</h3>
                  <p className="text-sm text-slate-500" data-id="element-156" data-mp="2woxdci8mbokg">Recomendado: 1200 × 630 px</p>
                </div>
                <span className="text-xs font-mono text-slate-400 shrink-0" data-id="element-157" data-mp="2rnhtuum39q46">1.91:1</span>
              </div>
              <div className="aspect-[40/21] overflow-hidden rounded-lg border border-slate-800 bg-navy px-[12%] py-[16%] text-white flex flex-col items-center justify-center text-center" data-id="element-158" data-mp="bw2bqwmrdcz7">
                <Logo dark size="lg" data-id="element-159" data-mp="fe9kc4lp8rk6" />
                <p className="mt-[7%] max-w-[78%] text-[clamp(0.7rem,1.8vw,1.65rem)] leading-relaxed text-slate-300" data-id="element-160" data-mp="jhfydyf1zn17">
                  Entiende una licitación mejor que cualquier competidor.
                </p>
              </div>
              <p className="text-sm text-slate-500 mt-4" data-id="element-161" data-mp="afyb9w38al30">
                Usa el componente de logo compartido en tamaño grande: misma geometría, espaciado y proporción que en la web. Sin grilla, métricas ni texto adicional.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-162" data-mp="13786fxycc21y">
              <h3 className="font-semibold text-navy" data-id="element-163" data-mp="og9434o3m9qt">Favicon</h3>
              <p className="text-sm text-slate-500 mt-1" data-id="element-164" data-mp="tekbz9uu2a6e">Recomendado: 32 × 32 px</p>
              <div className="mt-6 flex h-32 items-center justify-center rounded-lg bg-navy border border-slate-800" data-id="element-165" data-mp="1h4ohxnvhum0k">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-techblue" data-id="element-166" data-mp="1lwy9t9g65w5h">
                  <svg width="37" height="34" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Símbolo PRISMA" data-id="element-167" data-mp="1vqke6ypzwrtk">
                    <path d="M13 2 L24 22 L2 22 Z" stroke="#FFFFFF" strokeWidth="2.6" strokeLinejoin="round" data-id="element-168" data-mp="1qqzgermkapif" />
                  </svg>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4" data-id="element-169" data-mp="1hrkt211oyv25">
                Usar solo el triángulo blanco sobre azul tecnológico. Sin palabra ni detalle interior.
              </p>
            </div>
          </div>
        </Section>

        {/* 07 Iconografía y motion */}
        <Section num="07" title="Iconografía y movimiento" subtitle="Íconos lineales de lucide-react y animaciones snappy, siempre con propósito." data-id="element-170" data-mp="132g3tzwhbqkj">
          <div className="rounded-xl border border-slate-200 bg-white p-8 mb-6" data-id="element-171" data-mp="u31kdkeqcg9j">
            <div className="flex flex-wrap gap-6" data-id="element-172" data-mp="p3ov6t0dyx3s">
              {ICONS.map((Icon, i) => <Icon key={i} className="w-7 h-7 text-techblue" data-id="element-173" data-mp="30qsdg3oxrpv2" data-mpi={i} />)}
            </div>
            <p className="text-sm text-slate-500 mt-5" data-id="element-174" data-mp="35pv7kt3dobo9">
              lucide-react · trazo lineal · importación con sufijo Icon · color
              techblue o slate-400.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-8" data-id="element-175" data-mp="3eqb9hy4h3ar0">
            <ul className="text-sm text-slate-600 space-y-2" data-id="element-176" data-mp="3eogwd5zg0mxh">
              <li data-id="element-177" data-mp="2i1lqu5l0ue3z">
                <span className="font-semibold text-navy" data-id="element-178" data-mp="2i25b3sy59el6">Entrada:</span> fade +
                desplazamiento sutil (y: 16 → 0), duración ~0.5s.
              </li>
              <li data-id="element-179" data-mp="2r0qshkrvqnkd">
                <span className="font-semibold text-navy" data-id="element-180" data-mp="2r078zzmkzr48">Scroll:</span>{' '}
                whileInView con once: true — revelar una sola vez.
              </li>
              <li data-id="element-181" data-mp="1ycuumcqkqpm0">
                <span className="font-semibold text-navy" data-id="element-182" data-mp="1yba1mrjwreq9">Stagger:</span>{' '}
                retardo escalonado de 0.05–0.1s entre elementos.
              </li>
              <li data-id="element-183" data-mp="wo2ewgkbzaqs">
                <span className="font-semibold text-navy" data-id="element-184" data-mp="wnk9atbs3k67">Hover:</span> solo en
                elementos interactivos (botones, enlaces, tarjetas).
              </li>
            </ul>
          </div>
        </Section>
      </main>

      <footer className="bg-navy text-slate-400 py-10 border-t border-slate-800" data-id="element-185" data-mp="15pjp5ib24i7k">
        <div className="container mx-auto px-4 max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-186" data-mp="11mx3jz4zbtit">
          <Logo dark data-id="element-187" data-mp="1fldc7vmeju3l" />
          <p className="text-sm" data-id="element-188" data-mp="1fkv5g5245xhf">© 2026 PRISMA · Sistema de diseño</p>
        </div>
      </footer>
    </div>;
}